import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { HeadlineXL } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FixtureSelector } from 'components/fixture/FixtureSelector';
import { isFixtureClosed, isFixtureLive, isFixtureOpened } from 'lib/so5';

import { FixtureRewardsLayout_So5Fixture } from './__generated__/layout.graphql';

const Header = styled(FullWidthContainer)`
  display: grid;
  grid-template-columns: 1fr;
  background: var(--c-black);
  padding: var(--triple-unit) var(--container-padding);

  @media ${laptopAndAbove} {
    grid-template-columns: repeat(3, 1fr);

    > * {
      &:first-child {
        grid-column: 2/3;
      }
      &:nth-child(2) {
        grid-column: 3/4;
        > * {
          width: min-content;
          margin-left: auto;
        }
      }
    }
  }
`;
const FixtureSelectorContainer = styled.div`
  max-width: 430px;
  margin: auto;
`;
const StyledHeadlineXL = styled(HeadlineXL)`
  font-weight: normal;
`;
const MobileTabsContainer = styled.div`
  background-color: var(--c-black);
  border-radius: var(--triple-unit);
`;

enum Tabs {
  ALL = 'ALL',
  ONLY_MY_COMPETITIONS = 'ONLY_MY_COMPETITIONS',
}

const tabMessages: Record<Tabs, MessageDescriptor> = {
  [Tabs.ALL]: glossary.all,
  [Tabs.ONLY_MY_COMPETITIONS]: {
    id: 'rewards.FixtureRewards.myCompetitions',
    defaultMessage: 'My competitions',
  },
};

type Props = {
  so5Fixture: FixtureRewardsLayout_So5Fixture;
};

export const FixtureRewardsLayout = ({ so5Fixture }: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();

  const hasContenders = so5Fixture.mySo5LeaderboardContenders.nodes.length > 0;

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.ALL);

  if (!so5Fixture || !sport) {
    return null;
  }

  if (!hasContenders && activeTab === Tabs.ONLY_MY_COMPETITIONS) {
    setActiveTab(Tabs.ALL);
  }

  const tabs = (
    <TabBar value={activeTab}>
      {Object.values(Tabs).map(tab => (
        <Tab
          key={tab}
          value={tab}
          onClick={() => setActiveTab(tab)}
          disabled={!hasContenders && tab === Tabs.ONLY_MY_COMPETITIONS}
        >
          <FormattedMessage {...tabMessages[tab]} />
        </Tab>
      ))}
    </TabBar>
  );

  return (
    <>
      {!isLaptopAndAbove && (
        <Portal id="above-bottom-bar-portal">
          <MobileTabsContainer>{tabs}</MobileTabsContainer>
        </Portal>
      )}
      <Vertical gap={2}>
        <PageHeader text={glossary.rewardsPool} />
        <FullWidthContainer.NoPadding>
          <Vertical gap={2}>
            <Header>
              <StyledHeadlineXL as="p" className="text-center">
                {so5Fixture.displayName}
              </StyledHeadlineXL>
              {isLaptopAndAbove && tabs}
            </Header>
            <FixtureSelectorContainer>
              <FixtureSelector
                withMask
                currentFixture={so5Fixture}
                navigateTo={fixture =>
                  generateSportContextPath(
                    ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_REWARDS,
                    {
                      sport,
                      params: {
                        eventType: so5Fixture.type.toLowerCase(),
                        fixture,
                      },
                    }
                  )
                }
              />
            </FixtureSelectorContainer>
          </Vertical>
        </FullWidthContainer.NoPadding>
        <Outlet
          context={{
            withActualRewards: isFixtureClosed(so5Fixture),
            withEligibleRewards: isFixtureLive(so5Fixture),
            withRewardsOverview: isFixtureOpened(so5Fixture),
            onlyMyCompetitions: activeTab === Tabs.ONLY_MY_COMPETITIONS,
          }}
        />
      </Vertical>
    </>
  );
};

FixtureRewardsLayout.fragments = {
  So5Fixture: gql`
    fragment FixtureRewardsLayout_So5Fixture on So5Fixture {
      slug
      displayName
      type
      mySo5LeaderboardContenders {
        nodes {
          slug
        }
      }
      ...FixtureSelector_so5Fixture
      ...isFixtureClosed_so5Fixture
      ...isFixtureLive_so5Fixture
      ...isFixtureOpened_so5Fixture
    }
    ${FixtureSelector.fragments.so5Fixture}
    ${isFixtureClosed.fragments.so5Fixture}
    ${isFixtureLive.fragments.so5Fixture}
    ${isFixtureOpened.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureRewardsLayout_So5Fixture>,
};
